import { useState, useEffect, useRef } from "react";
import { Pencil, Lock, MessageCircle, RefreshCw, Wand2, Check, X, ChevronRight, Shield, Loader2, Trash2, Sparkles, HelpCircle, TriangleAlert, Circle } from "lucide-react";
import { QuestionEditModal } from "@/components/QuestionEditModal";
import { ItemQualitySummary } from "@/components/ItemQualitySummary";
import { RegenerateQuestionDialog, type BlueprintDecision } from "@/components/RegenerateQuestionDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { levelLabel } from "@/utils/levelLabels";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { apiClient } from "@/lib/apiClient";
import { readingLevelToGrade, type Question } from "@shared/schema";
import { answerLabel } from "@/utils/sortAnswers";
import {
  DONT_TEACH_KEY,
  DONT_TEACH_STEM_GUIDANCE,
  DONT_TEACH_OPTIONS_GUIDANCE,
  dontTeachGuidance,
  dontTeachOptionsMessage,
  dontTeachAdvisorySuggestion,
} from "@/utils/socratesDontTeach";
import {
  effectiveCycle,
  feedbackForCycle,
  isInternalPipelineMarker,
  isScored,
  type SocratesFeedbackEntry,
} from "@/utils/socratesScore";
import {
  bucketSocratesFindings,
  isSuggestionHidden,
} from "@/utils/socratesFeedback";
import {
  CRITERIA_LABELS,
  NOT_EVALUATED,
  judgmentCriterionKeys,
  notEvaluatedReason,
} from "@/utils/socratesCriteria";
import {
  duplicateAdvisoryText,
  resolveTargetDisplayNumber,
} from "@/utils/socratesDuplicate";

export interface SocratesQuestionData {
  question_index: number;
  task_id?: string;  // BCE stable identifier; may be absent on historical records
  final_score: number;
  final_passed: boolean;
  total_cycles: number;
  cycles: Array<{
    cycle_number: number;
    score: number;
    passed: boolean;
    error_flags: Record<string, number>;
    issues: string[];
    gate_applied: string;
    gate1_issues: string[];
    gate2_agents: string[];
    major_failures: string[];
    minor_failures: string[];
    /** Per-cycle advisory rewrites. The live BCE payload places
     *  quality_feedback inside each cycle object (pairs to THAT cycle's
     *  error_flags/issues) — see feedbackForCycle. Absent on
     *  question-level-era rows. */
    quality_feedback?: SocratesFeedbackEntry[];
  }>;
  /** Present when BCE's CDE (Claim Discrimination Engine) dedup pass flagged
   *  this question as a near-duplicate of an earlier one in the same test.
   *  When set, `cycles` ends with a synthetic CDE dedup event (gate
   *  `cde_duplicate`, cycle 99) rather than a genuine SOCRATES cycle — the
   *  item's real score lives in an earlier gate's sibling row for the same
   *  (session_id, question_index), which `effectiveCycle` (utils/socratesScore)
   *  resolves. The card renders that real score exactly as if the dedup row
   *  didn't exist, and ships the ARC-43 duplicate advisory below it: the
   *  marker's three-digit offset is not the teacher's item number, so the
   *  target is resolved through the session index→task_id map (see
   *  utils/socratesDuplicate) to the number actually on screen.
   *  `duplicate_of_question_index` is 0-based but can be `null` if BCE
   *  couldn't recover it from the raw event text. `entailment_score` is
   *  currently always `null` — a known, separately-tracked BCE-side gap. */
  duplicate_info?: {
    duplicate_of_question_index: number | null;
    entailment_score: number | null;
  } | null;
  /** Per-item advisory rewrites from BCE's analyzer. Each entry is fully
   *  attributed: `criterion` names the snake_case criterion key the finding
   *  belongs to (the authoritative pairing — see the Issues found bucketing
   *  in this card), `description` is the human-readable finding text, and
   *  `suggestion` is the actionable rewrite guidance. Historical rows may
   *  carry `criterion: "unknown"`, which renders in the card's Unattributed
   *  section rather than under a guessed criterion name.
   *
   *  NOTE: on live payloads this array lives PER CYCLE (the cycle's own
   *  `quality_feedback` field — the authoritative source for the cycle being
   *  displayed); this question-level field is the legacy/dev-sample location
   *  and only a fallback. See feedbackForCycle in utils/socratesScore. */
  quality_feedback?: SocratesFeedbackEntry[];
}

// Criterion display names and the not-evaluated presentation constants live in
// utils/socratesCriteria (shared with the Edit modal so the two views can't
// drift). NOT_EVALUATED (-1) is how BCE encodes a criterion it could not
// evaluate (ARC-149).
interface QuestionCardProps {
  question: Question;
  number: number;
  onUpdate: (question: Question) => void;
  onRegenerate: (questionId: string, targetLevel: Question["level"], blueprintDecision?: BlueprintDecision) => void;
  onRegenerateAnswer: (questionId: string, answerIndex: number) => void;
  /** Delete the whole question. */
  onDelete: (questionId: string) => void;
  /** Append a new answer option and fill it via the regenerate-option path.
   *  Kept on the interface for backward compatibility with current callers,
   *  but the inline "Add option" affordance has moved to the Edit modal.
   *  Safe to remove once callers stop passing it. */
  onAddOption?: (questionId: string) => void;
  /** Topic name for the question — forwarded to the Edit modal so its
   *  "Add option" generator can pass topic context to Blacksmith. */
  topicName?: string;
  /** The user's chosen reading level for the parent test — forwarded to
   *  the Edit modal so regenerate calls send the correct grade instead
   *  of a hardcoded default. */
  readingLevel?: string;
  /** Raw lesson text from the parent test's materials — forwarded to the
   *  Edit modal so its score and add-option calls let SOCRATES run
   *  Content-Appropriate Stem. Undefined when the test has no materials. */
  sourceText?: string;
  onChat: (question: Question) => void;
  socratesData?: SocratesQuestionData;
  socratesLoading?: boolean;
  onSocratesUpdate?: (questionId: string, score: SocratesQuestionData) => void;
  /** Store a fresh display-aligned rescore: merge score + cleared map onto
   *  the parent's CURRENT matching question without touching its content, so
   *  a newer edit or regeneration completed during the request is reverted
   *  by neither the stale snapshot nor the map it carried. `submitted` is the
   *  stem/answers/correct answer that were POSTed; the parent applies the
   *  result ONLY if the current question still matches it, discarding a
   *  stale response whose content was superseded mid-flight. */
  onSocratesRescore?: (
    questionId: string,
    score: SocratesQuestionData,
    submitted: { stem?: string; answers?: string[]; correctAnswer?: number },
  ) => void;
  /** Session-level BCE question_index → task_id map (built from the Socrates
   *  score response, see utils/socratesDuplicate). Lets a duplicate marker's
   *  target index resolve to a stable task identity. */
  questionIndexToTaskId?: Map<number, string>;
  /** The full question list — the duplicate target is looked up by its
   *  stable task_id so the advisory can cite the CURRENT display number
   *  after reordering or edits. */
  allQuestions?: Question[];
  /** Whether this surface can amend the blueprint (the creation flow can;
   *  the saved-test editor cannot). Gates the Update/Keep blueprint prompt
   *  in the regenerate dialog. */
  canAmendBlueprint?: boolean;
  /** Whole-question regen in progress — card gets a grey overlay + spinner. */
  isRegeneratingQuestion?: boolean;
  /** Indices of answer choices currently being regenerated — each row shows
   *  an inline spinner in place of the answer text. */
  regeneratingAnswerIndices?: number[];
  /** Indices of newly-added answer choices being generated for the first
   *  time — shown with a "Generating Option…" spinner instead of "Regenerating". */
  addingAnswerIndices?: number[];
  /** Show the per-item lock/unlock toggle. Defaults to true; the Edit Test
   *  page hides it. (Lock UI is currently hidden everywhere, but the prop +
   *  logic are kept so it can be re-enabled.) */
  showLock?: boolean;
  /** Highlights a freshly-added ("new") or just-regenerated ("regenerated")
   *  item: blue border + badge until the user has looked at it (in view ~5s),
   *  then onSeen fires to clear it. */
  highlight?: "new" | "regenerated";
  /** Indices of answer options that were just regenerated — each gets its own
   *  blue border + "Regenerated" label (rather than highlighting the whole
   *  item), cleared by the same onSeen when the item has been looked at. */
  regeneratedOptions?: number[];
  onSeen?: (questionId: string) => void;
  /** Clear a single option's "Regenerated" highlight + its toast (badge click). */
  onClearOption?: (questionId: string, index: number) => void;
}

// Lock UI is hidden for now (the per-item lock toggle). The lock logic, props,
// and state are all kept — flip this to true to bring the toggle back.
const SHOW_LOCK_UI = false;

// "Question N" label hidden for now — flip to true to show item numbers again.
const SHOW_ITEM_NUMBER = false;

export function QuestionCard({
  question,
  number,
  onUpdate,
  onRegenerate,
  onRegenerateAnswer,
  onAddOption,
  onDelete,
  topicName,
  readingLevel,
  sourceText,
  onChat,
  socratesData,
  socratesLoading = false,
  onSocratesUpdate,
  onSocratesRescore,
  questionIndexToTaskId,
  allQuestions,
  canAmendBlueprint = true,
  isRegeneratingQuestion = false,
  regeneratingAnswerIndices = [],
  addingAnswerIndices = [],
  showLock = true,
  highlight,
  regeneratedOptions = [],
  onSeen,
  onClearOption,
}: QuestionCardProps) {
  const isAnswerRegenerating = (i: number) => regeneratingAnswerIndices.includes(i);
  const isAnswerAdding = (i: number) => addingAnswerIndices.includes(i);
  // Either path shows the inline spinner; only the wording differs.
  const isAnswerBusy = (i: number) => isAnswerRegenerating(i) || isAnswerAdding(i);
  // Only one option may be (re)generated at a time per item: each call sends
  // the current options to the model, so a second concurrent call wouldn't see
  // the first's result and could duplicate or clash. Disable the other
  // option/add controls while any one is in flight.
  const anyAnswerBusy =
    regeneratingAnswerIndices.length > 0 || addingAnswerIndices.length > 0;
  const [localQuestion, setLocalQuestion] = useState(question);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // null means "follow the default": open if there are violations, otherwise
  // closed. A user click sets it to true/false explicitly. The default is
  // restored whenever a fresh score arrives (see effect below).
  const [socratesOpenOverride, setSocratesOpenOverride] = useState<boolean | null>(null);
  const [isRescoring, setIsRescoring] = useState(false);
  const [isRegenDialogOpen, setIsRegenDialogOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Sync local display state when the question prop changes (e.g. after regen).
  useEffect(() => {
    setLocalQuestion(question);
  }, [question]);

  // New-item highlight: once a freshly-added card has been in view for ~5s,
  // tell the parent it's been seen so the blue border fades and the badge goes
  // away. The timer only runs while the card is actually visible, so an item
  // the user hasn't scrolled to yet keeps its highlight.
  const hasOptionRegen = regeneratedOptions.length > 0;
  useEffect(() => {
    if ((!highlight && !hasOptionRegen) || !onSeen) return;
    const el = cardRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => onSeen(question.id), 5000);
        } else if (timer) {
          clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [highlight, hasOptionRegen, onSeen, question.id]);

  // When the lock UI is hidden (showLock=false, e.g. the Edit Test page),
  // locking isn't a concept here — render every item as unlocked regardless of
  // its stored flags, so pre-locked items aren't stuck non-editable. The stored
  // flags are preserved (onUpdate / save are untouched). While the lock UI is
  // hidden (SHOW_LOCK_UI=false) every item renders as unlocked so a previously
  // locked item isn't stuck non-editable with no toggle to free it.
  const lockActive = SHOW_LOCK_UI && showLock;
  const locked = lockActive ? localQuestion.locked : false;
  const stemLocked = lockActive ? localQuestion.stemLocked : false;
  const answersLocked = lockActive ? localQuestion.answersLocked : false;

  // A stem-less (blank) item has no question to anchor a regeneration against,
  // so per-option and whole-item regenerate are hidden until it's filled in
  // (via Edit or AI). Regenerating an option here would just return something
  // unrelated to the item or its topic.
  const isBlank = !localQuestion.stem.trim();

  const handleRescore = async () => {
    if (!onSocratesUpdate) return;
    setIsRescoring(true);
    try {
      const response = await apiClient("/api/questions/score", {
        method: "POST",
        body: JSON.stringify({
          stem: localQuestion.stem,
          answers: localQuestion.answers,
          correctAnswer: localQuestion.correctAnswer,
          targetGrade: readingLevelToGrade(readingLevel),
          ...(sourceText ? { sourceText } : {}),
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      // The isolated /api/questions/score endpoint scores a single item with
      // no session context, so it can never evaluate duplication: a rescored
      // item must keep the session-level duplicate evidence it came in with,
      // or its advisory would silently vanish on the first rescore.
      const displayedScore = data.duplicate_info == null && socratesData?.duplicate_info
        ? { ...data, duplicate_info: socratesData.duplicate_info }
        : data;
      // The rescore POSTed the displayed answers, so its letters already
      // match the rows: the score is display-aligned and must never ride next
      // to a stale sort map. The parent applies it ONLY if the current
      // question still matches the content that was POSTed (a newer edit or
      // regeneration completed while the request was in flight would
      // otherwise be overwritten by a score computed for the old answers) and
      // otherwise discards the stale response from both the map and the
      // persisted score. Never write the card's pre-request localQuestion
      // snapshot back.
      const submitted = {
        stem: localQuestion.stem,
        answers: localQuestion.answers,
        correctAnswer: localQuestion.correctAnswer,
      };
      if (onSocratesRescore) {
        onSocratesRescore(localQuestion.id, displayedScore, submitted);
      } else if (onSocratesUpdate) {
        onSocratesUpdate(localQuestion.id, displayedScore);
      }
    } catch (err) {
      console.error("[Socrates] Rescore failed:", err);
    } finally {
      setIsRescoring(false);
    }
  };

  // Toggle lock
  const handleLockToggle = () => {
    const updated = { ...localQuestion, locked: !localQuestion.locked };
    setLocalQuestion(updated);
    onUpdate(updated);
  };

  // Reset the user's open/closed override whenever a new score arrives so
  // the auto-open-on-violation default kicks back in (e.g. after a regen
  // adds new flags, the panel pops open on its own).
  useEffect(() => {
    setSocratesOpenOverride(null);
  }, [socratesData]);

  // Toggle the Socrates detail panel; when opening, scroll the BOTTOM of the
  // whole item pane into view so it's obvious what popped down.
  const toggleSocrates = (open: boolean) => {
    setSocratesOpenOverride(open);
    if (open) {
      requestAnimationFrame(() =>
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }),
      );
    }
  };

  return (
    <Card
      ref={cardRef}
      id={`question-card-${question.id}`}
      className={cn(
        // duration-1000 so the blue border/glow fades out smoothly once the
        // item has been seen (highlight clears).
        "relative transition-all duration-1000",
        locked && "bg-green-50 border-muted",
        // Blue border + soft glow; both fade out (no border-width change, so no
        // layout jump) when the item has been seen.
        highlight && "border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.20)]",
      )}
      data-testid={`question-${question.id}`}
    >
      {isRegeneratingQuestion && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-background/70 backdrop-blur-[1px]"
          aria-live="polite"
          data-testid={`regenerating-question-${question.id}`}
        >
          <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-card border shadow-xs text-sm">
            <Loader2 className="w-4 h-4 animate-spin text-secondary" />
            <span className="font-medium">Regenerating question…</span>
          </div>
        </div>
      )}
      <CardContent
        className={`pt-6 ${isRegeneratingQuestion ? 'pointer-events-none select-none' : ''}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {highlight && (
              <Badge
                onClick={() => onSeen?.(question.id)}
                title="Dismiss"
                className="gap-1 bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
                data-testid={`badge-${highlight}-${question.id}`}
              >
                {highlight === "regenerated" ? (
                  <RefreshCw className="w-3 h-3" />
                ) : (
                  <Sparkles className="w-3 h-3" />
                )}
                {highlight === "regenerated" ? "Regenerated" : "New"}
              </Badge>
            )}
            {SHOW_ITEM_NUMBER && (
              <span className="text-sm font-semibold text-muted-foreground">
                Question {number}
              </span>
            )}
            {question.standards && question.standards.length > 0 && (
              <>
                {question.standards.map((standardCode) => (
                  <Badge
                    key={standardCode}
                    variant="secondary"
                    className="text-xs font-mono"
                  >
                    {standardCode}
                  </Badge>
                ))}
              </>
            )}
            <span
              data-testid={`badge-cognitive-level-${question.id}`}
              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                question.level === "Knowledge"
                  ? "bg-blue-100 text-blue-700"
                  : question.level === "Understanding"
                  ? "bg-orange-100 text-orange-600"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {levelLabel(question.level)}
            </span>
            {socratesLoading ? (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Scoring...
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {SHOW_LOCK_UI && showLock && (
              <Button
                size="icon"
                variant="ghost"
                onClick={handleLockToggle}
                data-testid={`button-lock-${question.id}`}
                title={localQuestion.locked ? 'Unlock' : 'Lock'}
              >
                {localQuestion.locked ? (
                  <Lock className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <img
                    src="/unlock-icon.png"
                    alt="Unlocked"
                    className="w-4 h-4"
                  />
                )}
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={locked}
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  title="Delete item"
                  data-testid={`button-delete-${question.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this question?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the item from the test.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(question.id)}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Question Stem */}
        <div className="mb-4">
          <div
            className={`p-3 rounded-lg ${locked || stemLocked ? 'bg-muted' : ''}`}
            data-testid={`stem-display-${question.id}`}
          >
            <p className="text-lg font-medium">{localQuestion.stem}</p>
          </div>
          {question.duplicate_warning?.flagged && question.duplicate_warning.reasons.length > 0 && (
            <div
              className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
              data-testid={`duplicate-warning-${question.id}`}
            >
              <TriangleAlert className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
              <div className="text-xs text-amber-700 space-y-0.5">
                <p className="font-semibold">This item may be similar to an existing item</p>
                {question.duplicate_warning.reasons.map((reason, i) => (
                  <p key={i}>{reason}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Answer Choices - Editable */}
        <div className="space-y-2">
          {localQuestion.answers.map((answer, index) => {
            const optionRegen = regeneratedOptions.includes(index);
            return (
            <div
              key={index}
              className={cn(
                "group flex items-start gap-3 p-3 rounded-lg transition-all duration-1000",
                isAnswerBusy(index)
                  ? "bg-muted/60 cursor-wait pointer-events-none"
                  : locked || answersLocked
                  ? "bg-muted/50"
                  : "",
                // Blue outline on a just-regenerated option; fades once seen.
                optionRegen && "border border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.18)]",
              )}
              data-testid={`answer-${question.id}-${index}`}
            >
              <div
                className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  index === localQuestion.correctAnswer
                    ? 'border-success bg-success/10'
                    : 'border-border'
                }`}
              >
                {index === localQuestion.correctAnswer && (
                  <div className="w-2.5 h-2.5 rounded-full bg-success" />
                )}
              </div>

              {isAnswerBusy(index) ? (
                // Replace the entire option content (label + text + edit
                // hint + regenerate button) with a single spinner line.
                // min-h-6 matches the height contributed by the
                // regenerate Button (h-6) in normal mode, so the row keeps
                // its natural height. text-base matches the answer text's
                // font size so the loading line sits on the same baseline
                // as the radio dot does in the surrounding rows.
                <div
                  className="flex-1 flex items-center gap-2 text-base text-muted-foreground italic min-h-6"
                  aria-live="polite"
                >
                  <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                  <span>
                    {isAnswerAdding(index)
                      ? "Generating Option…"
                      : `Regenerating answer ${answerLabel(index)}…`}
                  </span>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <span className="font-medium text-foreground mr-2">
                        {answerLabel(index)})
                      </span>
                      <span className="text-foreground">{answer}</span>
                    </div>
                    {optionRegen && (
                      <Badge
                        onClick={() => onClearOption?.(question.id, index)}
                        title="Dismiss"
                        className="gap-1 shrink-0 bg-blue-500 text-white hover:bg-blue-600 cursor-pointer"
                        data-testid={`badge-option-regenerated-${question.id}-${index}`}
                      >
                        <RefreshCw className="w-3 h-3" />
                        Regenerated
                      </Badge>
                    )}
                  </div>
                  {!locked && !answersLocked && !isBlank && (
                    <div className="flex items-center gap-1 ml-2">
                      {index === localQuestion.correctAnswer ? (
                        // Regenerating the correct answer can break the
                        // stem/answer relationship — warn first.
                        <AlertDialog>
                          <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={anyAnswerBusy}
                              className="h-6 px-2 text-xs"
                              title="Regenerate this answer option"
                              data-testid={`button-regenerate-answer-${question.id}-${index}`}
                            >
                              Regenerate Option <Wand2 className="w-3 h-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Regenerate the correct answer?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This asks for the correct answer to this question again — usually a
                                clearer rewording of the same answer, keeping your stem unchanged. If
                                the option marked correct is actually wrong, it can fix that too. To
                                change what the question asks, regenerate the whole item instead.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onRegenerateAnswer(question.id, index)}
                              >
                                Regenerate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={anyAnswerBusy}
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRegenerateAnswer(question.id, index);
                          }}
                          title="Regenerate this answer option"
                          data-testid={`button-regenerate-answer-${question.id}-${index}`}
                        >
                          Regenerate Option <Wand2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* The inline "Add option" affordance lives in the Edit modal — see
            QuestionEditModal — so it isn't rendered from the card surface. */}

        {/* Solution Steps - Collapsible */}
        {question.solutionSteps && question.solutionSteps.length > 0 && (
          <Collapsible className="mt-4">
            <CollapsibleTrigger className="text-sm text-muted-foreground hover:underline">
              View Solution Steps
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ol className="list-decimal list-inside text-sm space-y-1 mt-2">
                {question.solutionSteps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </CollapsibleContent>
          </Collapsible>
        )}

                {/* Socrates Score Detail - Collapsible */}
        {socratesData && socratesData.cycles.length > 0 && (() => {
          // BCE's de-dup gate (cde_duplicate, cycle 99) and parse-failure gate
          // (parse_failure, cycle 5) write an unscored row with empty
          // error_flags. Render the item's real score from the scored sibling
          // row for the same (session_id, question_index) instead. When no
          // sibling exists — or the resolved cycle genuinely evaluated zero
          // criteria — "0/0 criteria passed" would be a passing grade for an
          // item that was never analysed, so render an honest unscored state
          // (never a green shield).
          const cycle = effectiveCycle(socratesData.cycles);
          const unscored = !isScored(cycle);

          // ARC-43 duplicate advisory: a CDE dedup marker's target index is a
          // BCE session position, not the teacher's item number. Resolve it
          // via the session index→task_id map into the current questions
          // array; fall back to the numberless wording when the target is
          // gone, unscored, or never carried a task_id (never guess a number).
          const targetDisplayNumber = resolveTargetDisplayNumber(
            socratesData.duplicate_info?.duplicate_of_question_index,
            questionIndexToTaskId,
            allQuestions ?? [],
          );
          const duplicateAdvisory = socratesData.duplicate_info
            ? (
              <div
                className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
                data-testid="duplicate-advisory"
              >
                <TriangleAlert className="w-4 h-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-700">
                  {duplicateAdvisoryText(targetDisplayNumber)}
                </p>
              </div>
            )
            : null;

          if (unscored) {
            const socratesOpen = socratesOpenOverride ?? true;
            return (
              <>
                <Collapsible open={socratesOpen} onOpenChange={toggleSocrates} className="mt-4">
                  <CollapsibleTrigger
                    className="flex items-center gap-2 text-base font-semibold rounded-md hover:opacity-80 transition-opacity text-foreground"
                    data-testid={`socrates-unscored-${question.id}`}
                  >
                    <Shield className="w-4 h-4 shrink-0" />
                    Item Quality: not analysed
                    <ChevronRight className={cn("w-4 h-4 transition-transform", { "rotate-90": socratesOpen })} />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-3 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        This item hasn't been scored by the quality pipeline, so
                        there are no criteria to display. Use Rescore to analyse
                        it now.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isRescoring}
                        onClick={handleRescore}
                        className="h-7 text-xs gap-1"
                      >
                        {isRescoring ? (
                          <>Rescoring… <Loader2 className="w-3 h-3 animate-spin" /></>
                        ) : (
                          <>Rescore <RefreshCw className="w-3 h-3" /></>
                        )}
                      </Button>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                {duplicateAdvisory}
              </>
            );
          }

          const lastCycle = cycle!;
          // The live Blacksmith payload doesn't always match the declared type —
          // a freshly-scored cycle can come back without error_flags/issues yet.
          // Default them so the panel can't throw and white-screen the page
          // while a test is coming back.
          const flags = lastCycle.error_flags ?? {};
          // Internal pipeline markers (no rule-family prefix, e.g.
          // cde_duplicate_*, parse_failure) must never reach the teacher.
          const issues = (lastCycle.issues ?? []).filter(
            (raw) => !isInternalPipelineMarker(raw),
          );
          const entries = Object.entries(flags);
          const failedEntries = entries.filter(([, v]) => v === 1);
          const passedEntries = entries.filter(([, v]) => v === 0);
          const notEvaluatedEntries = entries.filter(([, v]) => v === NOT_EVALUATED);
          const failedCount = failedEntries.length;
          const passedCount = passedEntries.length;
          const notEvaluatedCount = notEvaluatedEntries.length;
          // ARC-149: not-evaluable criteria contribute to neither the
          // passed numerator nor the evaluated denominator.
          const evaluatedCount = entries.length - notEvaluatedCount;

          // "Don't Teach (in Stem or Options)" (BCE's internal code "Z") is a
          // judgment call rather than a defect: its criterion is in the
          // judgment state, rendered with a distinct blue "?" presentation
          // instead of the standard red X (ARC-223 — see judgmentCriterionKeys
          // and the criteria grid below). Its raw "Z: Stem ..." / "Z:
          // Option(s) ..." messages are skipped by the bucketing loop: a bare
          // "Z:" tag is too short to match the snake_case criterion-prefix
          // regex, so without this exclusion they would leak as raw text
          // instead of the tailored guidance box (ARC-12 / ARC-219).

          // Bucket issue descriptions by criterion. BCE's quality_feedback
          // array is the PRIMARY source: each entry carries the exact
          // snake_case criterion key it belongs to (plus a human-readable
          // description and an optional rewrite suggestion), so a finding
          // like "Q: Option A contains 'not'" pairs to its flag without
          // parsing the description text. Entries whose criterion is
          // "unknown" (or can't be matched to a criterion this cycle
          // actually flagged) are held aside for the Unattributed section —
          // never zipped onto a criterion whose name would be wrong.
          //
          // Live payload shape: the array lives PER CYCLE — inside the cycle
          // object whose error_flags/issues this surface renders — not at the
          // question level. feedbackForCycle reads the displayed cycle's own
          // array first and only falls back to the legacy question-level
          // field, so a freshly-deployed item attributes correctly while
          // historical stored rows and the dev sample keep working.
          //
          // When quality_feedback is absent the issues-parsing path below
          // stays as the fallback.
          const rawFeedback = feedbackForCycle(cycle, socratesData?.quality_feedback);
          // ARC-223: a criterion is in the judgment state when it is flagged
          // and its quality_feedback entry carries severity=advisory — or it
          // is Don't Teach, the temporary hardcoded fallback (keyed by
          // DONT_TEACH_KEY; see judgmentCriterionKeys). Judgment criteria
          // render a blue marker instead of the red X.
          const judgmentKeys = judgmentCriterionKeys(flags, rawFeedback);
          // ARC-354: findings are bucketed by their OWN severity. Advisory
          // findings are judgment calls (blue, Needs your judgment); every
          // other finding is a genuine failure (red, Issues found). A
          // criterion can carry one advisory AND one failed entry, and then
          // appears under both headings below — its findings never mix.
          const {
            failed: failedIssuesByCriterion,
            judgment: judgmentIssuesByCriterion,
            failedSuggestions: failedSuggestionsByCriterion,
            judgmentSuggestions: judgmentSuggestionsByCriterion,
            unattributed: unattributedFeedback,
          } = bucketSocratesFindings({
            flags,
            feedback: rawFeedback,
            rawIssues: lastCycle.issues ?? [],
            filteredIssues: issues,
            judgmentKeys,
          });
          // ARC-354: an "Issues found" entry is a criterion with at least one
          // failed finding; a "Needs your judgment" entry is a criterion with
          // at least one judgment finding. A criterion with BOTH kinds counts
          // under BOTH headings, so the tally may exceed 16. A criterion
          // flagged with no findings at all still renders its existing
          // fallback row — under Issues found as a plain failure, or under
          // Needs your judgment when it is in the judgment state (e.g. Don't
          // Teach with no detail on a given cycle).
          const failureEntries = entries.filter(([k, v]) =>
            v === 1 &&
            (((failedIssuesByCriterion[k]?.length ?? 0) > 0) ||
              (!judgmentKeys.has(k) && !((judgmentIssuesByCriterion[k]?.length ?? 0) > 0))),
          );
          const judgmentEntries = entries.filter(([k, v]) =>
            v === 1 &&
            (((judgmentIssuesByCriterion[k]?.length ?? 0) > 0) ||
              (judgmentKeys.has(k) && !((failedIssuesByCriterion[k]?.length ?? 0) > 0))),
          );
          const judgmentCount = judgmentEntries.length;
          const summaryFailedCount = failureEntries.length;

          const hasViolations = failedCount > 0;
          const socratesOpen = socratesOpenOverride ?? hasViolations;

          // Which tailored guidance to show — scoped off BCE's raw (pre-tidy)
          // issue text rather than the orphan-matching heuristic above, which
          // can't reliably attribute a bare "Z:" tag to this criterion when
          // other criteria also fail in the same cycle. The Don't Teach
          // criterion's own quality_feedback descriptions also count toward
          // which half fired, so the heading and body stay correct when the
          // finding only arrived through that channel. Shared with the Edit
          // modal so the two surfaces can't drift.
          const {
            showStem: dontTeachShowStemGuidance,
            showOptions: dontTeachShowOptionsGuidance,
            optionGroups: dontTeachOptionGroups,
            stemMessages: dontTeachStemMessages,
            heading: dontTeachHeading,
          } = dontTeachGuidance(flags, lastCycle.issues ?? [], rawFeedback);

          // The duplicate advisory must be visible even when the effective
          // cycle passes every criterion. A passing score auto-collapses the
          // detail panel, so the warning can't live inside CollapsibleContent:
          // it renders as a sibling after the panel, always on screen.
          return (
            <>
              <Collapsible open={socratesOpen} onOpenChange={toggleSocrates} className="mt-4">
                {/* ARC-359: shield, "Item Quality:", the commas and the
                    arrow always read dark; the counts carry the tones. */}
                <CollapsibleTrigger
                  className="flex items-center gap-2 text-base font-semibold rounded-md hover:opacity-80 transition-opacity text-foreground"
                >
                  <Shield className="w-4 h-4 shrink-0" />
                  <ItemQualitySummary
                    passed={passedCount}
                    failed={summaryFailedCount}
                    notEvaluated={notEvaluatedCount}
                    judgment={judgmentCount}
                  />
                  <ChevronRight className={cn("w-4 h-4 transition-transform", { "rotate-90": socratesOpen })} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-3 space-y-3">
                    {/* All criteria — pass and fail — in one grid. The Issues
                        Found section below repeats the failed ones alongside
                        their descriptions so the failure and its explanation
                        sit together. */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      {entries.map(([key, value]) => {
                        // ARC-359: the icons carry the status, the name stays
                        // dark for every status. A flagged criterion with BOTH
                        // a failed and a judgment finding shows both markers
                        // side by side (red X then blue ?), routed by the same
                        // ARC-354 buckets that place it under both headings.
                        // A flagged criterion with no findings at all keeps
                        // its fallback marker: blue "?" in the judgment state
                        // (Don't Teach, advisory criteria), red X otherwise.
                        const notEvaluated = value === NOT_EVALUATED;
                        const hasFailedFinding =
                          (failedIssuesByCriterion[key]?.length ?? 0) > 0;
                        const hasJudgmentFinding =
                          (judgmentIssuesByCriterion[key]?.length ?? 0) > 0;
                        const noFindings =
                          !hasFailedFinding && !hasJudgmentFinding;
                        // ARC-206: the not-evaluated EXPLANATION lives in the
                        // "Not evaluated" section below Issues found — the
                        // checklist row keeps only the grey circle symbol +
                        // name.
                        return (
                          <div key={key} className="flex items-center gap-2">
                            {notEvaluated ? (
                              // ARC-206: neutral grey circle — never red or
                              // amber, visually distinct from the green check
                              // and the red X.
                              <Circle
                                className="w-3.5 h-3.5 text-muted-foreground shrink-0"
                                data-testid={`not-evaluated-symbol-${key}`}
                              />
                            ) : value === 0 ? (
                              <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                            ) : (
                              <>
                                {hasFailedFinding ||
                                  (noFindings && !judgmentKeys.has(key)) ? (
                                  <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                ) : null}
                                {hasJudgmentFinding ||
                                  (noFindings && judgmentKeys.has(key)) ? (
                                  <HelpCircle className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                                ) : null}
                              </>
                            )}
                            <span className="text-xs">
                              {CRITERIA_LABELS[key] || key}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* ARC-240: judgment findings (Don't Teach) render in their
                        own blue group between the criteria grid and Issues
                        Found. The full block moves whole — heading, advice
                        paragraphs, and any advisory suggestion — recoloured
                        from orange to blue-600. */}
                    {judgmentEntries.length > 0 && (
                      <div className="space-y-2" data-testid={`socrates-judgment-group-${question.id}`}>
                        <p
                          className="text-sm font-semibold text-foreground"
                          data-testid={`socrates-judgment-heading-${question.id}`}
                        >
                          Needs your judgment
                        </p>
                        {judgmentEntries.map(([key]) => {
                          const isDontTeach = key === DONT_TEACH_KEY;
                          const desc = judgmentIssuesByCriterion[key] ?? [];
                          const suggestions = judgmentSuggestionsByCriterion[key] ?? [];
                          return (
                            <div
                              key={key}
                              className="space-y-0.5"
                              data-testid={isDontTeach
                                ? `socrates-judgment-dont-teach-${question.id}`
                                : `socrates-judgment-row-${key}`
                              }
                            >
                              <p className="text-xs font-semibold text-blue-600">
                                {isDontTeach ? dontTeachHeading : (CRITERIA_LABELS[key] || key)}
                              </p>
                              {isDontTeach ? (
                                <>
                                  {dontTeachShowStemGuidance &&
                                    (dontTeachStemMessages.length > 0 ? (
                                      dontTeachStemMessages.map((message, i) => (
                                        <p key={i} className="text-xs text-blue-600">
                                          {message}
                                        </p>
                                      ))
                                    ) : (
                                      <p className="text-xs text-blue-600">
                                        {DONT_TEACH_STEM_GUIDANCE}
                                      </p>
                                    ))}
                                  {dontTeachShowOptionsGuidance &&
                                    (dontTeachOptionGroups.length > 0 ? (
                                      dontTeachOptionGroups.map((group, i) => (
                                        <p key={i} className="text-xs text-blue-600">
                                          {dontTeachOptionsMessage(group)}
                                        </p>
                                      ))
                                    ) : (
                                      <p className="text-xs text-blue-600">
                                        {DONT_TEACH_OPTIONS_GUIDANCE}
                                      </p>
                                    ))}
                                  {dontTeachAdvisorySuggestion(rawFeedback) && (
                                    <p
                                      className="text-xs text-blue-600 italic mt-1"
                                      data-testid="criterion-suggestion-do_not_teach_in_stem"
                                    >
                                      {dontTeachAdvisorySuggestion(rawFeedback)}
                                    </p>
                                  )}
                                </>
                              ) : desc.length > 0 ? (
                                <>
                                  {desc.map((d, i) => (
                                    <div key={i} className="space-y-0.5">
                                      <p className="text-xs text-blue-600">• {d}</p>
                                      {!isSuggestionHidden(key) && suggestions[i]?.length > 0 && (
                                        <p
                                          className="text-xs text-blue-600 italic"
                                          data-testid={`criterion-suggestion-${key}`}
                                        >
                                          {suggestions[i]}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <p className="text-xs text-blue-600 italic">
                                  Flagged, no details available.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ARC-240: every entry here is a genuine failure — Don't
                        Teach no longer reaches this section (judgmentEntries
                        above owns it), so the list keeps only the red
                        rendering path. */}
                    {(failureEntries.length > 0 || unattributedFeedback.length > 0) && (
                      <div className="space-y-2" data-testid="issues-found-section">
                        <p className="text-sm font-semibold text-foreground">Issues found</p>
                        {failureEntries.map(([key]) => {
                          const desc = failedIssuesByCriterion[key] ?? [];
                          return (
                            <div key={key} className="space-y-0.5">
                              <p className="text-xs font-semibold text-red-700">
                                {CRITERIA_LABELS[key] || key}
                              </p>
                              {desc.length > 0 ? (
                                desc.map((d, i) => {
                                  // Each quality_feedback description may carry
                                  // a rewrite suggestion — render it as a second
                                  // italic line beneath the description, in a
                                  // distinctly lighter red, with its own
                                  // data-testid so a spec can assert it.
                                  const sg = (failedSuggestionsByCriterion[key] ?? [])[i] ?? "";
                                  return (
                                    <div key={i} className="space-y-0.5">
                                      <p className="text-xs text-red-600">• {d}</p>
                                      {!isSuggestionHidden(key) && sg.length > 0 && (
                                        <p
                                          className="text-xs text-red-500 italic"
                                          data-testid={`criterion-suggestion-${key}`}
                                        >
                                          {sg}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })
                              ) : (
                                <p className="text-xs text-red-600 italic">
                                  Flagged, no details available.
                                </p>
                              )}
                            </div>
                          );
                        })}
                        {unattributedFeedback.length > 0 && (
                          <div className="space-y-2" data-testid="unattributed-feedback">
                            <p className="text-sm font-semibold text-foreground">Unattributed</p>
                            {unattributedFeedback.map((f, i) => (
                              <div key={i} className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">{f.description}</p>
                                {!isSuggestionHidden(f.criterion) && f.suggestion.length > 0 && (
                                  <p className="text-xs text-muted-foreground italic">
                                    {f.suggestion}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ARC-206: not-evaluated criteria get their own section
                        directly beneath Issues found (which stays a list of
                        defects to act on). Each row names the criterion and
                        records what was missing, taking the reason from the
                        analyzer's not_evaluated map when it carries one. */}
                    {notEvaluatedEntries.length > 0 && (
                      <div className="space-y-2" data-testid="not-evaluated-section">
                        <p className="text-sm font-semibold text-foreground">Not evaluated</p>
                        {notEvaluatedEntries.map(([key]) => (
                          <div key={key} className="space-y-0.5">
                            <p className="text-xs font-semibold text-muted-foreground">
                              {CRITERIA_LABELS[key] || key}
                            </p>
                            <p className="text-xs text-muted-foreground italic">
                              {notEvaluatedReason(lastCycle, key)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isRescoring}
                      onClick={handleRescore}
                      className="h-7 text-xs gap-1"
                    >
                      {isRescoring ? (
                        <>Rescoring… <Loader2 className="w-3 h-3 animate-spin" /></>
                      ) : (
                        <>Rescore <RefreshCw className="w-3 h-3" /></>
                      )}
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              {duplicateAdvisory}
            </>
          );
        })()}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4 border-t mt-4">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChat(localQuestion)}
            data-testid={`button-chat-${question.id}`}
          >
            Chat with AI about the item <MessageCircle className="w-4 h-4" />
          </Button>
          {!isBlank && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsRegenDialogOpen(true)}
              disabled={locked}
              data-testid={`button-regenerate-${question.id}`}
            >
              Regenerate item <RefreshCw className="w-4 h-4" />
            </Button>
          )}
          {!locked && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setIsEditModalOpen(true)}
              data-testid={`button-edit-${question.id}`}
            >
              Edit <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>

        {isEditModalOpen && (
          <QuestionEditModal
            question={localQuestion}
            initialSocratesData={socratesData}
            topicName={topicName}
            readingLevel={readingLevel}
            sourceText={sourceText}
            // Other items on the test, for the server's cross-item duplicate
            // check on the add-option path. Excludes the item being edited.
            existingItems={(allQuestions ?? [])
              .filter((q) => q.id !== question.id)
              .map((q) => ({ stem: q.stem, answers: q.answers }))}
            onSave={(updatedQuestion, score) => {
              onUpdate(updatedQuestion);
              onSocratesUpdate?.(updatedQuestion.id, score);
              setIsEditModalOpen(false);
            }}
            onClose={() => setIsEditModalOpen(false)}
          />
        )}
        <RegenerateQuestionDialog
          question={question}
          open={isRegenDialogOpen}
          onOpenChange={setIsRegenDialogOpen}
          canAmendBlueprint={canAmendBlueprint}
          onConfirm={(targetLevel, blueprintDecision) => {
            onRegenerate(question.id, targetLevel, blueprintDecision);
          }}
        />
      </CardContent>
    </Card>
  );
}
